import React, { useState } from 'react';
import { Twitter, Share2, Check, Copy } from 'lucide-react';

interface ArticleShareProps {
    title: string;
    url?: string;
    slug?: string;
}

export const ArticleShare: React.FC<ArticleShareProps> = ({ title, url, slug }) => {
    const [copied, setCopied] = useState(false);

    // Compute absolute clean canonical URL
    const articleUrl = url || (slug
        ? `https://openwatersyndicate.com/the-log/${slug}`
        : typeof window !== 'undefined' ? window.location.href : 'https://openwatersyndicate.com/the-log'
    );

    const encodedUrl = encodeURIComponent(articleUrl);
    const encodedTitle = encodeURIComponent(`"${title}" by @openwater.syndicate`);

    // Intent Links
    const xShareUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&hashtags=LakeOntario,OpenWaterSyndicate,SUP`;
    const threadsShareUrl = `https://www.threads.net/intent/post?text=${encodedTitle}%20${encodedUrl}`;

    // Copy Link to Clipboard
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(articleUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (err) {
            console.error('Failed to copy link', err);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-5 sm:pt-6 mt-6 sm:mt-8 border-t border-slate-800/80">
            {/* Share Label */}
            <span className="w-full sm:w-auto text-[10px] sm:text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-1 sm:mb-0 sm:mr-2">
                <Share2 className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                <span>Share Dispatch:</span>
            </span>

            {/* Share to X (Twitter) */}
            <a
                href={xShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 sm:px-3.5 sm:py-1.5 rounded-lg bg-black hover:bg-slate-900 border border-slate-700 hover:border-slate-500 text-white text-[11px] sm:text-xs font-semibold tracking-wide transition-all shadow-sm group"
                title="Share on X.com"
            >
                <svg
                    className="w-3.5 h-3.5 fill-current shrink-0"
                    viewBox="0 0 24 24"
                >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span>Post on X</span>
            </a>

            {/* Share to Threads */}
            <a
                href={threadsShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 sm:px-3.5 sm:py-1.5 rounded-lg bg-slate-950 hover:bg-slate-900 border border-slate-700 hover:border-purple-500/50 text-white text-[11px] sm:text-xs font-semibold tracking-wide transition-all shadow-sm group"
                title="Share on Threads"
            >
                <svg
                    className="w-3.5 h-3.5 fill-current text-white group-hover:text-purple-400 transition-colors shrink-0"
                    viewBox="0 0 24 24"
                >
                    <path d="M12.186 24C5.46 24 0 18.54 0 11.814 0 5.088 5.46 0 12.186 0c6.642 0 12.066 5.28 12.186 11.916v.444c0 3.75-2.616 6.54-6.222 6.54-2.184 0-4.086-1.092-4.992-2.826-.786 1.47-2.316 2.4-4.272 2.4-2.898 0-5.1-2.202-5.1-5.1 0-2.898 2.202-5.1 5.1-5.1 1.776 0 3.258.828 4.134 2.148V9.162c0-2.28-1.74-3.792-4.224-3.792-2.166 0-3.882 1.254-4.11 2.97h-2.16c.264-2.928 2.82-5.13 6.27-5.13 3.708 0 6.384 2.376 6.384 5.952v7.218c0 2.502 1.632 4.182 3.978 4.182 2.658 0 4.182-2.112 4.182-4.716 0-5.46-4.38-9.84-9.84-9.84-5.46 0-9.84 4.38-9.84 9.84 0 5.46 4.38 9.84 9.84 9.84z" />
                </svg>
                <span>Threads</span>
            </a>

            {/* Quick Copy Link Button */}
            <button
                onClick={handleCopy}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 sm:px-3 sm:py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-[11px] sm:text-xs font-medium transition-all"
                title="Copy article link"
            >
                {copied ? (
                    <>
                        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span className="text-emerald-400 font-mono">Link Copied!</span>
                    </>
                ) : (
                    <>
                        <Copy className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>Copy Link</span>
                    </>
                )}
            </button>
        </div>
    );
};